import React, { useEffect, useRef, useState } from 'react';
import { css } from '@emotion/react';
import t from 'react-translate';

import { config } from '@config/pendo.config.json';
import { AIEvaluationStatus } from 'redux/schemas/models/submissions';
import { dunes, seafoam } from 'athena/styles/colors';
import { black, hexToRgbaString, lowTide, success, warning } from 'styles/global_defaults/colors';
import { headerLineHeight, headerRegularLineHeight, lightFontWeight, normalFontWeight, openSans, textExtraLargeFontSize, textMediumFontSize } from 'styles/global_defaults/fonts';
import {
  halfSpacing,
  largeSpacing,
  standardSpacing,
  tripleSpacing,
} from 'styles/global_defaults/scaffolding';
import prodPathReplace from 'shared/prod-path-rewrite';
import DotsFlowAnimation from 'shared/components/dots-flow';
import NvConfetti from 'shared/components/nv-confetti';

type HeroVariant = {
  background: string;
  title: string;
  image: string;
  withConfetti: boolean;
};

const getVariant = (evaluation: AIEvaluationStatus): HeroVariant => {
  switch (evaluation) {
    case AIEvaluationStatus.EXCEEDS_EXPECTATIONS:
      return {
        background: hexToRgbaString(seafoam, 0.3),
        title: t.SUBMISSIONS.AI_ASSIGNMENT.RATING.AMAZING(),
        image: 'images/excellent.png',
        withConfetti: true,
      };
    case AIEvaluationStatus.NEEDS_IMPROVEMENT:
      return {
        background: hexToRgbaString(dunes, 0.3),
        title: t.SUBMISSIONS.AI_ASSIGNMENT.RATING.NEEDS_IMPROVEMENT(),
        image: 'images/need-work.png',
        withConfetti: false,
      };
    case AIEvaluationStatus.MEETS_EXPECTATIONS:
    default:
      return {
        background: hexToRgbaString(lowTide, 0.3),
        title: t.SUBMISSIONS.AI_ASSIGNMENT.RATING.GOOD(),
        image: 'images/good-job.png',
        withConfetti: false,
      };
  }
};

const styles = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: ${largeSpacing}px 0;
  gap: ${halfSpacing}px;

  img.image-badge {
    width: ${tripleSpacing}px;
    height: ${tripleSpacing}px;
  }

  .hero-text {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 1px;
    padding: 0 ${standardSpacing}px;
    text-align: center;
  }

  .hero-title {
    font-family: ${openSans};
    font-weight: ${lightFontWeight};
    font-size: ${textExtraLargeFontSize}px;
    line-height: ${headerRegularLineHeight}px;
    color: ${black};
    margin: 0;
  }

  .hero-reason {
    font-family: ${openSans};
    font-weight: ${normalFontWeight};
    font-size: ${textMediumFontSize}px;
    line-height: ${headerLineHeight}px;
    color: ${black};
    margin: 0;
  }
`;

type Props = {
  evaluation: AIEvaluationStatus;
  reason?: string;
};

const Hero: React.FC<Props> = ({ evaluation, reason }) => {
  const variant = getVariant(evaluation);
  const heroDataQa = config.pendo.submissionView.aiInsights.hero;
  const badgeDataQa = config.pendo.submissionView.aiInsights.heroBadge;

  const imgRef = useRef<HTMLImageElement>(null);
  const [isImageLoaded, setIsImageLoaded] = useState(false);

  useEffect(() => {
    setIsImageLoaded(false);
    // Safari doesn't fire onLoad for cached images; check complete after DOM update
    if (imgRef.current?.complete && imgRef.current.naturalWidth > 0) {
      setIsImageLoaded(true);
    }
  }, [variant.image]);

  const handleImageLoad = () => {
    setIsImageLoaded(true);
  };

  const handleImageError = () => {
    // Reveal the image on error so the browser shows the alt text fallback
    setIsImageLoaded(true);
  };

  const renderImage = () => (
    <div
      className={`rounded-circle ${isImageLoaded ? 'bg-white' : ''}`}
      data-qa={badgeDataQa}
    >
      <img
        ref={imgRef}
        className={`image-badge ${isImageLoaded ? '' : 'invisible'}`}
        src={prodPathReplace(variant.image)}
        alt={variant.title}
        onLoad={handleImageLoad}
        onError={handleImageError}
      />
    </div>
  );

  const renderBadge = () => {
    if (variant.withConfetti) {
      return (
        <NvConfetti
          numberOfStars={7}
          numberOfCircles={8}
          numberOfTriangles={3}
          starColor={success}
          circleColor={lowTide}
          triangleColor={warning}
          duration={2000}
          particleSize={{ width: 12, height: 12 }}
        >
          {renderImage()}
        </NvConfetti>
      );
    }

    return <DotsFlowAnimation>{renderImage()}</DotsFlowAnimation>;
  };

  return (
    <div
      css={[styles, css`background: ${variant.background};`]}
      data-qa={heroDataQa}
    >
      {renderBadge()}
      <div className='hero-text'>
        <h2 id='ai-insights-title' className='hero-title'>{variant.title}</h2>
        {reason && <p className='hero-reason'>{reason}</p>}
      </div>
    </div>
  );
};

export default Hero;
